import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Checkbox, Dialog, TextInput } from "fe-shared/src/components";

import styles from "./FlagDialog.module.scss";

// Matches Requests.FlagComment; a longer value is truncated by the driver, not rejected.
const COMMENT_LENGTH_LIMIT = 2000;

export interface FlagPayload {
  comment: string;
  showToParticipants: boolean;
}

export interface Props {
  isActive: boolean;
  onSubmit: (payload: FlagPayload) => void;
  onCancel: () => void;
  comment?: string;
  showToParticipants?: boolean;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const FlagDialog = (props: Props) => {
  const {
    isActive,
    onSubmit,
    onCancel,
    comment = "",
    showToParticipants = false,
    isEdit = false,
    isSubmitting = false,
  } = props;
  const { t } = useTranslation();

  const [commentDraft, setCommentDraft] = useState(comment);
  const [isVisibleToParticipant, setIsVisibleToParticipant] = useState(
    showToParticipants
  );
  const [isCommentMissing, setIsCommentMissing] = useState(false);

  useEffect(() => {
    if (isActive) {
      setCommentDraft(comment);
      setIsVisibleToParticipant(showToParticipants);
      setIsCommentMissing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const isTooLong = commentDraft.length > COMMENT_LENGTH_LIMIT;

  const handleCommentChange = (_event: unknown, newValue: string) => {
    setCommentDraft(newValue);

    if (isCommentMissing && newValue.trim()) {
      setIsCommentMissing(false);
    }
  };

  const handleSubmit = () => {
    const trimmed = commentDraft.trim();

    if (!trimmed || isTooLong) {
      setIsCommentMissing(!trimmed);
      return;
    }

    onSubmit({ comment: trimmed, showToParticipants: isVisibleToParticipant });
  };

  const errorText = isTooLong
    ? `${t("pickupRequests.flag.characterLimit")} ${commentDraft.length}/${COMMENT_LENGTH_LIMIT}`
    : t("pickupRequests.flag.commentRequired");

  return (
    <Dialog
      modal
      width={560}
      isActive={isActive}
      onClose={onCancel}
      title={t("pickupRequests.flag.title")}
      actions={[
        <Button
          id="flag-dialog-submit"
          key="submit"
          variant="filled"
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {t(`pickupRequests.flag.${isEdit ? "editFlag" : "addFlag"}`)}
        </Button>,
        <Button
          id="flag-dialog-cancel"
          key="cancel"
          variant="outlined"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {t("pickupRequests.flag.cancel")}
        </Button>,
      ]}
    >
      <TextInput
        isMultiline
        required
        id="flag-dialog-comment"
        value={commentDraft}
        disabled={isSubmitting}
        isError={isCommentMissing || isTooLong}
        errorText={errorText}
        onChange={handleCommentChange}
        wrapperClassName={styles.commentField}
      />
      <Checkbox
        id="flag-dialog-show-to-participant"
        title={t("pickupRequests.flag.showToParticipant")}
        value={isVisibleToParticipant}
        disabled={isSubmitting}
        onChange={(_event, checked) => setIsVisibleToParticipant(checked)}
      />
    </Dialog>
  );
};

export default FlagDialog;
